import { EditableTitle } from "@/components/EditableText";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { ArtifactMetadata } from "@/components/display/attributes/ArtifactMetadata";
import {
  SkillCategory,
  SkillCategorySelect,
  SkillContentFields,
} from "@/components/skill";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useExpertEditData } from "@/data/use-expert-edit-data";
import { useExpertSkillsData } from "@/data/use-expert-skills-data";
import { useGroupSkillsData } from "@/data/use-group-skills-data";
import { useKitData } from "@/data/use-kits-data";
import { useSkillData } from "@/data/use-skill-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUnlink } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

export const SkillPage = () => {
  const navigate = useNavigate();
  const groupSlug = useGroupSlug();
  const [unlinkModal, setUnlinkModal] = useState(false);

  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { expert } = useExpertEditData({ groupUuid, kitUuid });
  const expertUuid = expert?.uuid ?? null;

  const skillUuid = (
    useParams({ strict: false }) as Record<string, string | undefined>
  ).skillUuid;
  const { skills: groupSkills, isLoading: isLoadingSkills } =
    useGroupSkillsData({
      groupUuid,
    });
  const { skillUuids, dissociateSkill } = useExpertSkillsData(
    groupUuid,
    kitUuid,
    expertUuid,
  );
  const linkedSkills = groupSkills.filter((skill) =>
    skillUuids.includes(skill.uuid),
  );
  const skill = groupSkills.find((s) => s.uuid === skillUuid);
  const { updateKit } = useKitData(groupUuid, skill?.creatorKitUuid ?? null);

  const { updateSkill, skill: kitSkill } = useSkillData(
    groupUuid,
    skill?.creatorKitUuid ?? null,
    skill?.uuid ?? null,
  );

  if (isLoadingSkills) {
    return null;
  }

  if (!skill) {
    return (
      <Page document>
        <PageHeader>
          <PageHeader.Title>Skill not found</PageHeader.Title>
        </PageHeader>
      </Page>
    );
  }

  const handleTitleUpdate = async (newTitle: string) => {
    if (skill.creatorKitUuid) {
      await updateSkill({
        name: newTitle,
      });
      await updateKit({
        name: newTitle,
      });
    }
  };

  const handleContentUpdate = (html: string) => {
    if (skill.creatorKitUuid) {
      updateSkill({
        content: html,
      });
    }
  };

  const handleMetadataDescriptionUpdate = (metadataDescription: string) => {
    if (skill.creatorKitUuid) {
      updateSkill({
        metadataDescription,
      });
    }
  };

  const handleCategoryChange = (category: SkillCategory | null) => {
    if (skill.creatorKitUuid) {
      updateSkill({
        category: category,
      });
    }
  };

  const handleUnlink = async () => {
    if (!expertUuid || !skill.uuid) return;
    await dissociateSkill(skill.uuid);
    toast.success("Skill unlinked.");

    const remainingSkills = linkedSkills.filter((s) => s.uuid !== skill.uuid);
    if (remainingSkills.length > 0)
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/skills/$skillUuid",
        params: {
          groupSlug,
          kitUuid: kitUuid!,
          skillUuid: remainingSkills[0].uuid,
        },
      });
    else {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid",
        params: { groupSlug, kitUuid: kitUuid! },
      });
    }
  };

  return (
    <>
      <AppTopRightMenu.ContentPortal>
        <Button variant="outline" onClick={() => setUnlinkModal(true)}>
          <FontAwesomeIcon icon={faUnlink} />
          Unlink
        </Button>
      </AppTopRightMenu.ContentPortal>

      <Page document>
        <PageHeader>
          <PageHeader.Title>
            <EditableTitle value={skill.name} onUpdate={handleTitleUpdate} />
          </PageHeader.Title>
        </PageHeader>
        <PageBody innerClassName="gap-8" document>
          <PageBody.Metadata className="justify-between">
            <PageBody.MetadataGroup>
              <div className="flex grow-0 flex-row items-center gap-2">
                Category:
                <SkillCategorySelect
                  value={skill.category as SkillCategory | null}
                  onValueChange={handleCategoryChange}
                  includeNoneOption={true}
                  includeLabel={false}
                  className="grid gap-2"
                  disabled={skill.group?.uuid !== groupUuid}
                />
              </div>
            </PageBody.MetadataGroup>
            <PageBody.MetadataGroup>
              <ArtifactMetadata artifact={skill} />
            </PageBody.MetadataGroup>
          </PageBody.Metadata>

          <SkillContentFields
            skillUuid={skill.uuid}
            groupUuid={groupUuid!}
            kitUuid={(skill.creatorKitUuid ?? kitUuid)!}
            skillName={skill.name}
            metadataDescription={skill.metadataDescription}
            content={skill.content}
            dependencies={kitSkill?.dependencies ?? []}
            onMetadataDescriptionUpdate={handleMetadataDescriptionUpdate}
            onContentUpdate={handleContentUpdate}
            disabled={skill.group?.uuid !== groupUuid}
          />
        </PageBody>
      </Page>

      <UnlinkAlert
        open={unlinkModal}
        onOpenChange={setUnlinkModal}
        onConfirm={handleUnlink}
      />
    </>
  );
};

const UnlinkAlert = ({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>Unlink Skill</AlertDialogHeader>
        <AlertDialogDescription>
          <p>Do you want to unlink this skill from this expert?</p>
          <p>This will not delete the original skill.</p>
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => onOpenChange(false)}>
            Cancel
          </AlertDialogAction>
          <AlertDialogAction
            onClick={() => {
              onOpenChange(false);
              onConfirm();
            }}
            className="bg-destructive"
          >
            Unlink
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
